import { ElementType, forwardRef, HTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse, { domToReact } from 'html-react-parser'

import { nl2br, removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-helvetica text-s-h1 md:text-d-h1 -mt-[0.12em] -mb-[0.16em] md:-mt-[0.22em] md:-mb-[0.26em]',
      h2: 'font-helvetica text-s-h2 md:text-d-h2 -mt-[0.22em] -mb-[0.26em]',
      h3: 'font-helvetica text-s-h3 md:text-d-h3 -mt-[0.22em] -mb-[0.26em]',
      h4: 'font-helvetica text-s-h4 md:text-d-h4 -mt-[0.22em] -mb-[0.22em]',
      h5: 'font-helvetica text-s-h5 md:text-d-h5 -mt-[0.22em] -mb-[0.26em]',
      h6: 'font-helvetica text-s-h6 md:text-d-h6 -mt-[0.52em] -mb-[0.56em] md:-mt-[0.22em] md:-mb-[0.22em]',
      large: 'font-roboto text-large -my-[0.32em]',
      regular: 'font-roboto text-regular -my-[0.36em]',
      small: 'font-roboto text-small -my-[0.36em]',
      caption: 'font-roboto text-caption -my-[0.46em]',
      overline: 'font-roboto text-overline uppercase -my-[0.38em]'
    }
  },
  defaultVariants: {
    variant: 'regular'
  }
})

const childrenClasses = cn([
  '[&_b]:font-medium', // '[&_small]:text-[50%]'
  '[&_sup]:text-[0.6425em]',
  '[&_ul]:list-disc [&_ul]:pl-[1.45rem] [&_ul]:my-[2rem]',
  '[&_a]:underline [&_a:hover]:text-text-accent-light [&_a]:transition-colors [&_a]:duration-300 [&_a]:ease-out-cubic'
])

const options = {
  replace: (domNode: any) => {
    if (domNode.type === 'tag' && ['i', 'b', 'strong'].includes(domNode.name)) {
      const Tag = domNode.name
      return (
        <>
          {domNode.children.map((child: any, index: number) => {
            if (child.type === 'text') {
              return child.data.split(' ').map((word: string, wordIndex: number) => (
                <Tag key={`${index}-${wordIndex}`}>
                  {word}
                  {wordIndex < child.data.split(' ').length - 1 ? ' ' : ''}
                </Tag>
              ))
            }
            return domToReact(child, options)
          })}
        </>
      )
    }
  }
}

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    asChild: boolean
  }>

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    return (
      <Component
        className={cn(typographyVariants({ variant, className }), childrenClasses)}
        ref={ref}
        {...props}
      >
        {parse(children ? removeParagraph(nl2br(children as string)) : '', options)}
      </Component>
    )
  }
)

export default Typography
